/**
 * @file appStyles.js
 * @description Style helpers for app screens, built on the size scale (theme.SIZE). Use these instead of picking
 *   heights, paddings and font sizes per screen:
 *     controlBox   size of a button, input or select (32 px; 40 px with `touch` on phones). Spread it into a style
 *                  that keeps its own colors.
 *     appButton    a full button style (primary, secondary, ghost, danger, dangerOutline, success).
 *     appInput     a full text input / select style; appTextarea for multi-line fields.
 *     TEXT         page title (20), card title (15), body (13), secondary (12), eyebrow label (11).
 *     NUM / CODE   numbers use the body font with tabular figures; JetBrains Mono is only for codes (RBT-1432).
 *   Toolbar rows on Openings keep their own helper (toolbar/barStyles.js), which reads the same scale.
 * @module components/ui/appStyles
 */
import { T, SIZE } from "../../theme";

/** Numbers (counts, money, percents, dates): body font, tabular figures so columns line up. */
export const NUM = Object.freeze({ fontFamily: T.fontBody, fontVariantNumeric: "tabular-nums" });

/** Codes only (RBT-1432, case codes, CPT codes). */
export const CODE = Object.freeze({ fontFamily: T.fontMono });

/** Type roles. Spread one and add color or weight where the screen needs it. */
export const TEXT = Object.freeze({
  pageTitle: Object.freeze({ fontSize: SIZE.pageTitle, fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.01em", fontFamily: T.fontBody }),
  cardTitle: Object.freeze({ fontSize: SIZE.title, fontWeight: 700, lineHeight: 1.3, fontFamily: T.fontBody }),
  body:      Object.freeze({ fontSize: SIZE.text, lineHeight: 1.5 }),
  sm:        Object.freeze({ fontSize: SIZE.textSm, lineHeight: 1.45 }),
  label:     Object.freeze({ fontSize: SIZE.label, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }),
});

/** Height and padding for a control. Colors and borders stay with the caller. */
export function controlBox({ touch = false, iconOnly = false, compact = false } = {}) {
  const h = touch ? SIZE.controlTouch : SIZE.control;
  return {
    height: h, minWidth: iconOnly ? h : undefined, padding: iconOnly ? 0 : compact ? "0 10px" : "0 12px",
    boxSizing: "border-box", borderRadius: SIZE.radius, fontSize: SIZE.text, lineHeight: 1,
  };
}

const BUTTON_TONES = {
  primary:   { background: T.blue, color: "#fff", border: `1px solid ${T.blue}` },
  secondary: { background: T.surface, color: T.textMid, border: `1px solid ${T.borderStrong}` },
  ghost:     { background: "transparent", color: T.blue, border: "1px solid transparent" },
  danger:    { background: T.red, color: "#fff", border: `1px solid ${T.red}` },
  // A destructive action in a list row (Revoke, Cancel invite): red, quieter than the final confirm.
  dangerOutline: { background: T.surface, color: T.red, border: `1px solid ${T.red}66` },
  success:   { background: T.green, color: "#fff", border: `1px solid ${T.green}` },
};

/** A complete button style on the scale. */
export function appButton({ variant = "secondary", touch = false, iconOnly = false, disabled = false } = {}) {
  return {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, flexShrink: 0, whiteSpace: "nowrap",
    ...controlBox({ touch, iconOnly }),
    ...(BUTTON_TONES[variant] || BUTTON_TONES.secondary),
    fontWeight: 600, fontFamily: T.fontBody,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
    transition: "background 0.12s, color 0.12s, border-color 0.12s",
  };
}

/** A complete single-line input or select style on the scale. */
export function appInput({ touch = false, focused = false, disabled = false } = {}) {
  return {
    width: "100%", ...controlBox({ touch, compact: true }),
    border: `1px solid ${focused ? T.blue : T.borderStrong}`, outline: "none",
    background: disabled ? T.surfaceAlt : T.surface, color: disabled ? T.textLight : T.text,
    fontFamily: T.fontBody, boxShadow: focused ? T.shadowFocus : "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
  };
}

/** Multi-line field on the scale (no fixed height). */
export function appTextarea({ focused = false } = {}) {
  return {
    ...appInput({ focused }), height: "auto", minHeight: SIZE.control * 2, padding: "8px 10px", lineHeight: 1.5, resize: "vertical",
  };
}
